<template>
  <!-- Contact Form Modal -->
  <teleport to="body">
    <div
      v-if="showFormModal"
      class="modal-overlay"
      @click="$emit('closeFormModal')"
    >
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Contact Sales</h3>
        </div>

        <form class="contact-form" @submit.prevent="submitForm">
          <div id="name" class="form-group">
            <label for="name">Name*</label>
            <input id="name" v-model="form.name" type="text" required >
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="email">Work Email*</label>
              <input id="email" v-model="form.email" type="email" required >
            </div>
            <div class="form-group">
              <label for="phone">Phone Number</label>
              <div class="phone-input-group">
                <div class="country-code-select">
                  <b>+</b>
                  <input
                    v-model="countryCode"
                    type="text"
                    inputmode="numeric"
                    aria-label="Country code"
                    maxlength="3"
                    minlength="0"
                  >
                </div>
                <input
                  id="phone"
                  v-model="form.contactNumber"
                  type="tel"
                  pattern="[0-9].{4,}"
                  minlength="5"
                  maxlength="10"
                  class="phone-number-input"
                >
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="company"
                >Company Name{{ isEnterprise ? "*" : "" }}</label
              >
              <input
                id="company"
                v-model="form.companyName"
                type="text"
                :required="isEnterprise"
              >
            </div>
            <div v-if="isEnterprise" class="form-group">
              <label for="jobTitle">Job Title</label>
              <input id="jobTitle" v-model="form.jobTitle" type="text" >
            </div>
          </div>
          <div v-if="isEnterprise" class="form-group">
            <label for="companySize">Company Size*</label>
            <select id="companySize" v-model="form.companySize" required>
              <option value="">Select company size</option>
              <option v-for="size in companySizeArr" :key="size" :value="size">
                {{ size }} employees
              </option>
            </select>
          </div>
          <div class="form-group captcha-group">
            <ClientOnly>
              <NuxtTurnstile
                ref="turnstile"
                v-model="captchaToken"
                :options="{ theme: 'dark' }"
              />
            </ClientOnly>
          </div>
          <div class="form-actions">
            <div class="message" role="status" aria-live="polite">
              <p v-show="flash">{{ msg }}</p>
            </div>
            <div class="btns">
              <button type="button" class="cancel-btn" @click="closeForm">
                Cancel
              </button>
              <button
                type="submit"
                class="submit-btn"
                :disabled="!captchaToken"
              >
                Send Message
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </teleport>
</template>

<script setup>
// Imports
import logger from "debug";
import axios from "axios";
import { watch } from "vue";

//variables
const emit = defineEmits(["closeFormModal"]);
const config = useRuntimeConfig();
const debug = logger(
  "travvir:debut-nuxt:components:enterprise:EnterpriseContact",
);
let companySizeArr = ["1-10", "11-50", "51-200", "201-1000", "1000+"];
const form = reactive({
  name: "",
  email: "",
  contactNumber: "",
  companyName: "",
  jobTitle: "",
  companySize: "",
});
const msg = ref(null);
const flash = ref(false);
const countryCode = ref();
const captchaToken = ref("");
const turnstile = ref(null);

//props defines
const props = defineProps({
  showFormModal: {
    type: Boolean,
    default: false,
  },
  typeOfContactForm: {
    type: String,
    default: "",
  },
  isEnterprise: {
    type: Boolean,
    default: true,
  },
});

const resetCaptcha = () => {
  captchaToken.value = "";
  turnstile.value?.reset();
};

//functions
const submitForm = async () => {
  try {
    const mobileRegix = /^\+(?:[0-9] ?){6,14}[0-9]$/;
    const mobilenumber = `+${countryCode.value}${form.contactNumber}`;

    if (
      !(
        form.name &&
        form.email &&
        (!props.isEnterprise || (form.companyName && form.companySize))
      )
    )
      return;
    if (!captchaToken.value) {
      flash.value = true;
      msg.value = "Please complete the captcha";
      return;
    }
    if (form.contactNumber) {
      if (!mobileRegix.test(mobilenumber)) {
        flash.value = true;
        msg.value = "please enter valid mobile number";
        return;
      }
      flash.value = false;
    }
    //queue check
    const url = config.public.NUXT_API_URL + "/contact";
    const response = await axios.post(url, {
      name: form.name,
      email: form.email,
      mobile: mobilenumber,
      message: `
       company : ${form.companyName}
       jobTitle : ${form.jobTitle}
       companySize : ${form.companySize}
       msg: for ${props.typeOfContactForm}
      `,
      captchaToken: captchaToken.value,
    });
    if (response.status == 200) {
      flash.value = true;
      msg.value = "Message sent successfully";
      setTimeout(() => {
        closeForm();
      }, 3000);
      form.name = "";
      form.email = "";
      form.contactNumber = "";
      form.companyName = "";
      form.jobTitle = "";
      form.companySize = "";
    }
  } catch (error) {
    debug(error);
    flash.value = true;
    msg.value = "Something went wrong, please try again later.";
  }
};

const handleEscape = (e) => {
  if (e.key === "Escape" && props.showFormModal) {
    emit("closeFormModal");
  }
};

const closeForm = () => {
  msg.value = null;
  flash.value = false;
  emit("closeFormModal");
};

watch(
  () => props.showFormModal,
  () => {
    if (props.showFormModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
      resetCaptcha();
    }
  },
);

// Lifecycle hooks
onMounted(() => {
  document.addEventListener("keydown", handleEscape);
});
onUnmounted(() => {
  document.removeEventListener("keydown", handleEscape);
});
</script>
<style lang="scss" scoped>
// Modal Styles
.modal-overlay {
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 2rem;
  backdrop-filter: blur(5px);

  @include respond-below(sm) {
    padding: 1rem;
  }
}

.modal-content {
  background: #1a1a1a;
  border-radius: $radius-lg;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);

  @include respond-below(sm) {
    max-height: 95vh;
  }
}

.modal-header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  @include respond-below(sm) {
    padding: 1.5rem 1.5rem 1rem;
  }

  h3 {
    margin: 0;
    font-size: 2.8rem;
    font-weight: $fw-semibold;
    color: white;

    @include respond-below(sm) {
      font-size: $text-lg;
    }
  }
}

.contact-form {
  padding: 2rem;

  @include respond-below(sm) {
    padding: 1.5rem;
  }
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;

  @include respond-below(sm) {
    grid-template-columns: 1fr;
    gap: 0;
  }
}

#name {
  width: 100%;
}

.form-group {
  margin-bottom: 1rem;

  label {
    display: block;
    text-align: start;
    margin-bottom: 0.5rem;
    color: rgba(255, 255, 255, 0.9);
    font-weight: $fw-medium;
    font-size: $text-sm;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 0.75rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: $radius-sm;
    color: white;
    font-family: $theme-font;
    font-size: $text-sm;
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: #8a2be2;
      background: rgba(255, 255, 255, 0.08);
      box-shadow: 0 0 0 2px rgba(138, 43, 226, 0.2);
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }

  select {
    cursor: pointer;

    option {
      background: #1a1a1a;
      color: white;
    }
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }

  // Phone input group styles
  .phone-input-group {
    display: flex;
    gap: 0.5rem;

    .country-code-select {
      display: flex;
      align-items: center;
      min-width: 70px;
      max-width: 70px;
      padding: 0.75rem 0.5rem;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: $radius-sm;
      color: white;
      cursor: pointer;
      appearance: none;
      input {
        border: none;
        background: transparent;
        color: white;
        width: 100%;
        font-family: $theme-font;
        font-size: inherit;
        padding: 0;
        margin-left: 0.25rem;
        box-shadow: none;
        @include respond-below(sm) {
          padding: 0.5rem;
        }
      }

      @include respond-below(md) {
        min-width: 50px;
        max-width: 50px;
        font-size: $text-xxs;
        padding: 0.625rem 0.375rem;
      }

      @include respond-below(sm) {
        min-width: 30px;
        max-width: 30px;
        padding: 0.5rem 0.25rem;
        background-size: 6px;
      }

      &:focus-within {
        border-color: #8a2be2;
        background: rgba(255, 255, 255, 0.08);
        box-shadow: 0 0 0 2px rgba(138, 43, 226, 0.2);
      }
    }

    .phone-number-input {
      flex: 1;
      padding: 0.75rem;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: $radius-sm;
      color: white;
      font-family: $theme-font;
      font-size: $text-sm;
      transition: all 0.2s ease;

      @include respond-below(md) {
        padding: 0.625rem;
        font-size: $text-xs;
      }

      @include respond-below(sm) {
        padding: 0.5rem;
        font-size: $text-xxs;
      }

      &:focus {
        border-color: #8a2be2;
        background: rgba(255, 255, 255, 0.08);
        box-shadow: 0 0 0 2px rgba(138, 43, 226, 0.2);
      }

      &::placeholder {
        color: rgba(255, 255, 255, 0.4);
        font-size: inherit;
      }
    }

    @include respond-below(xs) {
      // flex-direction: column;
      gap: 0.25rem;

      .country-code-select {
        min-width: 20%;
        // max-width: unset;
      }
    }
  }
}

.captcha-group {
  display: flex;
  justify-content: flex-start;
  margin-top: 0.5rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 2rem;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @include respond-below(sm) {
    flex-direction: column;
  }
  .message {
    display: flex;
    @include respond-below(sm) {
      height: 40px;
      text-align: center;
    }
  }

  .btns {
    display: flex;
    gap: 1rem;
    @include respond-below(sm) {
      justify-content: center;
    }
  }
  button {
    padding: 0.75rem 1.5rem;
    border-radius: $radius-sm;
    font-weight: $fw-medium;
    font-size: $text-sm;
    cursor: pointer;
    transition: all 0.2s ease;
    font-family: $theme-font;
    @include respond-below(md) {
      padding: 0.825rem 1.25rem;
      font-size: $text-xs;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .cancel-btn {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.8);

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }
  }

  .submit-btn {
    background: #8a2be2;
    border: 1px solid #8a2be2;
    color: white;

    &:hover:not(:disabled) {
      background: #7a25c7;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(138, 43, 226, 0.3);
    }
  }
}
</style>
